import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

const ResetPasswordPage = () => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(true);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(i18n.language === 'fr' 
        ? 'Les mots de passe ne correspondent pas' 
        : 'Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error(i18n.language === 'fr' 
        ? 'Le mot de passe doit contenir au moins 8 caractères' 
        : 'Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Reset failed');
      }
      
      setSuccess(true);
      toast.success(i18n.language === 'fr' 
        ? 'Mot de passe réinitialisé avec succès !' 
        : 'Password reset successfully!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast.error(error.message || (i18n.language === 'fr' 
        ? 'Lien invalide ou expiré' 
        : 'Invalid or expired link'));
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <main className="min-h-screen pt-20 pb-12 flex items-center" data-testid="reset-password-error">
        <div className="max-w-md mx-auto px-4 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <XCircle className="w-10 h-10 text-red-600" />
          </motion.div>
          <h1 className="text-2xl font-bold mb-4">
            {i18n.language === 'fr' ? 'Lien invalide' : 'Invalid link'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {i18n.language === 'fr' 
              ? 'Ce lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.' 
              : 'This reset link is invalid or has expired. Please request a new link.'}
          </p>
          <Link to="/forgot-password">
            <Button className="gap-2">
              {i18n.language === 'fr' ? 'Demander un nouveau lien' : 'Request new link'}
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  if (success) {
    return (
      <main className="min-h-screen pt-20 pb-12 flex items-center" data-testid="reset-password-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-600" />
          </motion.div>
          <h1 className="text-2xl font-bold mb-4">
            {i18n.language === 'fr' ? 'Mot de passe réinitialisé !' : 'Password reset!'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {i18n.language === 'fr' 
              ? 'Votre mot de passe a été modifié avec succès. Vous allez être redirigé...' 
              : 'Your password has been changed successfully. Redirecting...'}
          </p>
          <Link to="/login">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              {i18n.language === 'fr' ? 'Aller à la connexion' : 'Go to login'}
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-20 pb-12 flex items-center" data-testid="reset-password-page">
      <div className="max-w-md mx-auto px-4 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl font-bold mb-2">
            {i18n.language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {i18n.language === 'fr' 
              ? 'Choisissez un nouveau mot de passe sécurisé.' 
              : 'Choose a new secure password.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password">
                {i18n.language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  placeholder="••••••••"
                  required
                  minLength={8}
                  data-testid="password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {i18n.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  placeholder="••••••••"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90 py-6"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading 
                ? (i18n.language === 'fr' ? 'Réinitialisation...' : 'Resetting...') 
                : (i18n.language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset password')}
            </Button>
          </form>
        </motion.div>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
